/**
 * Step 3 Mobile: Investments
 * Holdings as stacked cards (not table rows). Add Holding opens a bottom sheet
 * with ticker search. DB pensions as expandable cards. Swipe-to-delete on holdings.
 */

import { useState, useCallback, type FormEvent } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { Step3FormData, HoldingData, ContributionPersonData } from '../../types/wizard';
import { ContributionsSection } from './ContributionsSection';
import { type FundMetadataResponse } from '../../services/planApi';
import { searchFunds as searchFundsApi } from '../../services/planApi';
import { WizardMobileShell } from './WizardMobileShell';
import { BottomSheet } from '../mobile/BottomSheet';
import type { AllStepFormData } from '../../types/wizard';

const HoldingSchema = z.object({
  holdingId: z.string(),
  accountType: z.enum(['SIPP', 'ISA', 'BROKERAGE']),
  etfName: z.string().min(1, 'Fund name is required'),
  ticker: z.string().optional(),
  assetClass: z.enum(['EQUITY', 'BOND', 'MIXED', 'OTHER']),
  currentValue: z.number().positive('Value must be greater than 0'),
  ocfPct: z.number().min(0).max(0.1).optional(),
});

function makeStep3Schema(planType: 'SINGLE' | 'JOINT') {
  if (planType === 'JOINT') {
    return z.object({
      holdings: z.object({
        person0: z.array(HoldingSchema),
        person1: z.array(HoldingSchema),
      }),
    });
  }
  return z.object({
    holdings: z.object({
      person0: z.array(HoldingSchema),
    }),
  });
}

const accountLabels: Record<string, string> = { SIPP: 'SIPP', ISA: 'ISA', BROKERAGE: 'Brokerage' };

const ASSET_CLASS_LABELS: Record<string, string> = {
  EQUITY: 'Equities',
  BOND: 'Bonds',
  MIXED: 'Mixed',
  OTHER: 'Other',
};

interface Step3Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step3FormData) => void;
  onSave: (data: Step3FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

/** Bottom sheet for adding/editing a holding with ticker search */
function AddHoldingSheet({ open, onClose, onAdd, editing }: Readonly<{
  open: boolean; onClose: () => void;
  onAdd: (h: HoldingData) => void;
  editing?: HoldingData | null;
}>) {
  const [token, setToken] = useState(editing?.ticker || '');
  const [name, setName] = useState(editing?.etfName || '');
  const [acctType, setAcctType] = useState(editing?.accountType || 'SIPP');
  const [assetClass, setAssetClass] = useState(editing?.assetClass || 'EQUITY');
  const [value, setValue] = useState(editing?.currentValue?.toString() || '');
  const [ocf, setOcf] = useState(editing?.ocfPct ? (editing.ocfPct * 100).toString() : '');
  const [searchResults, setSearchResults] = useState<FundMetadataResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [valueError, setValueError] = useState('');

  const handleTickerSearch = useCallback(async (q: string) => {
    setToken(q);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const results = await searchFundsApi(q.trim());
      setSearchResults(results.slice(0, 8));
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  }, []);

  const selectFund = (fund: FundMetadataResponse) => {
    setToken(fund.ticker);
    setName(fund.name);
    setAssetClass(fund.assetClass === 'BOND' ? 'BOND' : fund.assetClass === 'MIXED' ? 'MIXED' : fund.assetClass === 'COMMODITY' ? 'OTHER' : 'EQUITY');
    if (fund.ocfPct != null) setOcf((fund.ocfPct * 100).toFixed(2));
    setSearchResults([]);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const numVal = parseFloat(value);
    if (!value || isNaN(numVal) || numVal <= 0) {
      setValueError('Value must be greater than £0');
      return;
    }
    setValueError('');
    const h: HoldingData = {
      holdingId: editing?.holdingId || crypto.randomUUID(),
      accountType: acctType as HoldingData['accountType'],
      etfName: name,
      ticker: token || undefined,
      assetClass: assetClass as HoldingData['assetClass'],
      currentValue: numVal,
      ocfPct: ocf ? parseFloat(ocf) / 100 : undefined,
      bondDetails: editing?.bondDetails,
    };
    onAdd(h);
    // Reset
    setToken(''); setName(''); setValue(''); setOcf(''); setSearchResults([]);
    onClose();
  };

  return (
    <BottomSheet open={open} onClose={onClose} title={editing ? 'Edit Holding' : 'Add Holding'}>
      <form onSubmit={handleSubmit} className="mobile-bottom-sheet-form">
        {/* Ticker search */}
        <label className="mobile-label">Search ticker or name</label>
        <input type="text" className="mobile-input" placeholder="e.g., VWRL" value={token}
          onChange={e => handleTickerSearch(e.target.value)} autoComplete="off" />
        {searching && <span className="mobile-hint">Searching...</span>}
        {searchResults.length > 0 && (
          <div className="mobile-search-results">
            {searchResults.map(f => (
              <button key={f.ticker} type="button" className="mobile-search-result" onClick={() => selectFund(f)}>
                <span className="mobile-search-result__ticker">{f.ticker}</span>
                <span className="mobile-search-result__name">{f.name}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mobile-bottomsheet-divider"><span>Fund details</span></div>

        <span className="mobile-hint">Search above to auto-fill, or type directly below.</span>
        <label className="mobile-label">Fund name</label>
        <input type="text" className="mobile-input" placeholder="e.g., Vanguard FTSE All-World" value={name} onChange={e => setName(e.target.value)} required />

        <label className="mobile-label">Account type</label>
        <div className="mobile-segmented-control">
          {(['SIPP', 'ISA', 'BROKERAGE'] as const).map(t => (
            <label key={t} className={`mobile-segment${acctType === t ? ' mobile-segment--active' : ''}`}>
              <input type="radio" value={t} className="mobile-segment-input" checked={acctType === t} onChange={() => setAcctType(t)} />
              <span>{accountLabels[t]}</span>
            </label>
          ))}
        </div>

        <label className="mobile-label">Current value (£)</label>
        <input type="number" className="mobile-input" placeholder="0" value={value} onChange={e => setValue(e.target.value)} required />
        {valueError && <span className="mobile-error">{valueError}</span>}

        <label className="mobile-label">Annual fund charge (%)</label>
        <span className="mobile-hint">Enter as percentage, e.g. 0.22 for 0.22% or 5 for 5%.</span>
        <input type="text" className="mobile-input" placeholder="e.g., 0.22" value={ocf}
          onChange={e => setOcf(e.target.value)}
          onBlur={() => {
            const text = ocf.trim();
            if (!text) return;
            const pct = Number.parseFloat(text);
            if (!Number.isNaN(pct) && pct >= 0) {
              setOcf(pct.toFixed(2));
            }
          }}
        />

        <button type="submit" className="mobile-btn mobile-btn--primary mobile-btn--full">{editing ? 'Save' : 'Add Holding'}</button>
      </form>
    </BottomSheet>
  );
}

export default function Step3HoldingsMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step3Props>) {
  const planType = (initialData?.planType || 'SINGLE') as 'SINGLE' | 'JOINT';
  const numPersons = planType === 'JOINT' ? 2 : 1;
  const schema = makeStep3Schema(planType);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingHolding, setEditingHolding] = useState<{ person: number } | null>(null);
  const [contributions, setContributions] = useState<Record<string, ContributionPersonData>>(() => initialData?.contributions || {});
  const [pensionExpanded, setPensionExpanded] = useState<Record<number, boolean>>({ 0: true });

  const initialHoldings = planType === 'JOINT'
    ? { person0: initialData?.holdings?.person0 || [], person1: initialData?.holdings?.person1 || [] }
    : { person0: initialData?.holdings?.person0 || [] };

  const { control, handleSubmit, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { holdings: initialHoldings },
  });

  const fieldArrays = [
    useFieldArray({ control, name: 'holdings.person0' as const }),
    useFieldArray({ control, name: 'holdings.person1' as const }),
  ];

  const holdingsData = watch('holdings');

  const handleBlur = useCallback(() => {
    const data = watch();
    onSave({ ...data, contributions } as Step3FormData);
  }, [watch, onSave, contributions]);

  const handleAddHolding = (holding: HoldingData) => {
    const pi = editingHolding?.person ?? 0;
    fieldArrays[pi].append(holding);
    setSheetOpen(false);
    setEditingHolding(null);
    setTimeout(handleBlur, 0);
  };

  const removeHolding = (personIndex: number, holdingIndex: number) => {
    fieldArrays[personIndex].remove(holdingIndex);
    setTimeout(handleBlur, 0);
  };

  const handleContributionsChange = useCallback((newC: Record<string, ContributionPersonData>) => {
    setContributions(newC);
    const data = watch();
    onSave({ ...data, contributions: newC } as Step3FormData);
  }, [watch, onSave]);

  return (
    <WizardMobileShell step={3} totalSteps={10} title="Investments" onBack={onBack} onContinue={() => handleSubmit(d => onNext({ ...d, contributions } as Step3FormData))()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <p className="mobile-step-desc">Add your pension (SIPP), ISA, and brokerage investments.</p>

        {planType === 'JOINT' && (
          <div className="mobile-segmented-control">
            {Array.from({ length: numPersons }, (_, i) => {
              const pn = initialData?.persons?.[i]?.fullName || `Person ${i + 1}`;
              return (
                <span key={i} className="mobile-segment mobile-person-label">
                  {pn}
                </span>
              );
            })}
          </div>
        )}

        {Array.from({ length: numPersons }, (_, pi) => {
          const holdings = (holdingsData as Record<string, HoldingData[]>)?.[`person${pi}`];
          const total = (holdings || []).reduce((s, h) => s + (h.currentValue || 0), 0);

          return (
            <div key={pi} className="mobile-holdings-section">
              {planType === 'SINGLE' && <h3 className="mobile-person-card__title">Your Investments</h3>}

              {(!holdings || holdings.length === 0) ? (
                <p className="mobile-hint">(no holdings yet)</p>
              ) : (
                <div className="mobile-holdings-list">
                  {holdings.map((h, hi) => (
                    <div key={h.holdingId} className="mobile-holding-card">
                      <span className={`mobile-holding-badge mobile-holding-badge--${h.accountType.toLowerCase()}`}>
                        {accountLabels[h.accountType]}
                      </span>
                      <div className="mobile-holding-card__body">
                        <span className="mobile-holding-card__name">{h.etfName || 'Unnamed holding'}</span>
                        {h.ticker && <span className="mobile-holding-card__ticker">Ticker: {h.ticker}</span>}
                        <span className="mobile-holding-card__info">
                          {ASSET_CLASS_LABELS[h.assetClass] ?? h.assetClass} {h.ocfPct ? `· ${(h.ocfPct * 100).toFixed(2)}% charge` : ''}
                        </span>
                        <span className="mobile-holding-card__value">
                          {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(h.currentValue)}
                        </span>
                      </div>
                      <button type="button" className="mobile-holding-card__delete" onClick={(e) => { e.stopPropagation(); removeHolding(pi, hi); }} aria-label="Remove holding">
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full" onClick={() => { setEditingHolding({ person: pi }); setSheetOpen(true); }}>
                + Add Holding
              </button>

              {total > 0 && <p className="mobile-holdings-total">Total: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(total)}</p>}

              {/* Pensions */}
              <div className="mobile-pensions-section">
                <button type="button" className="mobile-expand-toggle" onClick={() => setPensionExpanded(p => ({ ...p, [pi]: !p[pi] }))}>
                  {pensionExpanded[pi] ? '▾' : '▸'} Guaranteed pension income
                </button>
                {pensionExpanded[pi] && (
                  <div className="mobile-expand-section">
                    <p className="mobile-hint">Add any DB or workplace pensions providing guaranteed income.</p>
                    {/* Simplified pension list for mobile — in full build would use fieldArray */}
                    <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full">+ Add Pension</button>
                  </div>
                )}
              </div>

              {/* Contributions */}
              {initialData?.persons?.[pi] && (
                <ContributionsSection
                  persons={[initialData.persons[pi]]}
                  contributions={contributions[`person${pi}`] ? { person0: contributions[`person${pi}`] } : {}}
                  onChange={(updated) => {
                    if (updated.person0) handleContributionsChange({ ...contributions, [`person${pi}`]: updated.person0 });
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      <AddHoldingSheet open={sheetOpen} onClose={() => { setSheetOpen(false); setEditingHolding(null); }} onAdd={handleAddHolding} editing={editingHolding ? (holdingsData as Record<string, HoldingData[]>)?.[`person${editingHolding.person}`]?.[0] : null} />
    </WizardMobileShell>
  );
}
